import React from 'react';
import {
	Button,
	Drawer,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	Flex,
	Heading,
	DrawerBody,
	VStack,
	Avatar,
	Grid,
	Center,
	LoadingSpinner,
	Box,
	useToast,
} from '@tempo/core';
import {
	MeDocument,
	useArtistsPickerQuery,
	useRelatedArtistsLazyQuery,
	useValidateOnboardingArtistsPickerStepMutation,
} from '@graphql/generated';
import type {ArtistFragment} from '@graphql/generated';

import {useTranslation} from 'next-i18next';
import {MIN_ARTIST_TO_SELECT} from '@constants';
import {HeartFilledIcon} from '@tempo/icons';
import ArtistSearch from '@components/ArtistPickerDrawer/ArtistSearch';
import Toast, {INTENT} from '@components/Toast';

interface ArtistPickerDrawerProps {
	isOpen: boolean;
	onClose: () => void;
}

type ArtistPicker = ArtistFragment & {isSelected?: boolean};

const ArtistPickerDrawer = ({isOpen, onClose}: ArtistPickerDrawerProps) => {
	const {t} = useTranslation();

	const [artists, setArtists] = React.useState<ArtistPicker[]>([]);
	const [getRelatedArtists] = useRelatedArtistsLazyQuery();

	const toast = useToast({
		duration: 3000,
	});

	const {loading} = useArtistsPickerQuery({
		variables: {
			first: 100,
			endCursor: null,
		},
		skip: !isOpen || artists.length > 0,
		onCompleted: (data) => {
			setArtists(
				data.onboardingSteps?.artistsPicker.artists.edges
					?.filter(({node}) => node)
					.map(({node}) => node as ArtistPicker) ?? [],
			);
		},
	});

	const [validateOnboardingArtistsPickerStep] =
		useValidateOnboardingArtistsPickerStepMutation({
			variables: {
				input: {
					artistIds: artists
						.filter((artist) => artist.isSelected)
						.map((artist) => artist.id),
				},
			},
			update: (cache, nextData) => {
				if (nextData?.data?.validateOnboardingArtistsPickerStep) {
					cache.updateQuery(
						{
							query: MeDocument,
						},
						(data) => ({
							...data,
							me: {
								...data.me,
								onboarding: {
									...data.me.onboarding,
									shouldBeOnboarded:
										nextData.data?.validateOnboardingArtistsPickerStep
											.onboarding.shouldBeOnboarded,
								},
							},
						}),
					);
				}
			},
			onError: () => {
				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			},
		});

	const selectArtist = (artistId: string, artist?: ArtistFragment) => {
		getRelatedArtists({
			variables: {
				first: 4,
				artistId,
				endCursor: null,
				excludedArtistIds: artists.map((artist) => artist.id),
			},
			onCompleted: (data) => {
				const relatedArtists =
					data.onboardingSteps?.artistsPicker.relatedArtists.edges
						?.filter(({node}) => node)
						.map(({node}) => node as ArtistPicker) ?? [];

				const findIndex = artists.findIndex((artist) => artist.id === artistId);

				const newArtists = Array.from(
					artists.map((artist) => {
						if (artist?.id === artistId) {
							return {...artist, isSelected: true};
						}
						return artist;
					}),
				);

				newArtists.splice(findIndex + 1, 0, ...relatedArtists);
				if (artist && findIndex === -1) {
					newArtists.unshift({...artist, isSelected: true});
				}
				setArtists(newArtists);
			},
		});
	};

	const unselectArtist = (artistId: string) => {
		const newArtists = artists.map((artist) => {
			if (artist.id === artistId) {
				return {...artist, isSelected: false};
			}
			return artist;
		});
		setArtists(newArtists);
	};

	const nbSelectedItems = artists.filter((artist) => artist.isSelected).length;

	const isDisabled = nbSelectedItems < MIN_ARTIST_TO_SELECT;

	let copy = t(
		'artistselectionoptimization_action_continuewithXartistsUPP_web',
		{sprintf: [nbSelectedItems], count: nbSelectedItems},
	);

	if (!nbSelectedItems) {
		copy = t(
			'artistselectionoptimization_action_chooseatleastXartistsUPP_web',
			{
				sprintf: [MIN_ARTIST_TO_SELECT],
				count: MIN_ARTIST_TO_SELECT,
			},
		);
	}
	if (nbSelectedItems < MIN_ARTIST_TO_SELECT) {
		copy = t('artistselectionoptimization_action_chooseXmoreartistsUPP_web', {
			sprintf: [MIN_ARTIST_TO_SELECT - nbSelectedItems],
			count: MIN_ARTIST_TO_SELECT - nbSelectedItems,
		});
	}

	return (
		<Drawer isOpen={isOpen} onClose={onClose} size="full">
			<DrawerOverlay />
			<DrawerContent css={{height: '100dvh'}}>
				<DrawerCloseButton />
				<DrawerBody
					display="flex"
					alignSelf="center"
					justifyContent="space-between"
					w={['100%', '60%']}
					h="stretch"
					flexDir="column"
					p="spacing.l"
				>
					<ArtistSearch onAddArtist={selectArtist} />
					<Grid
						mt="spacing.l"
						overflow="scroll"
						overflowX="hidden"
						gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
						gap="spacing.s"
						css={{
							msOverflowStyle: 'none',
							scrollbarWidth: 'none',
							'&::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						{artists.map((artist) => (
							<VStack
								as="button"
								key={artist.id}
								spacing="spacing.s"
								textAlign="center"
								onClick={() =>
									artist.isSelected
										? unselectArtist(artist.id)
										: selectArtist(artist.id)
								}
							>
								<Box position="relative">
									<Avatar
										name={artist.name}
										size="size.3xl"
										src={artist.picture?.medium[0]}
										borderColor={
											artist.isSelected
												? 'border.accent.primary.default'
												: 'transparent'
										}
										borderWidth="2px"
										opacity={artist.isSelected ? '0.5' : 'null'}
										_hover={{
											opacity: '0.5',
										}}
									/>
									<HeartFilledIcon
										size="medium"
										color="icon.accent.primary.default"
										position="absolute"
										top="50%"
										left="50%"
										transform="translate(-50%, -50%)"
										opacity={artist.isSelected ? 1 : 0}
										transition="opacity 0.5s ease"
									/>
								</Box>

								<Heading
									variant="heading.xs"
									color="text.neutral.primary.default"
								>
									{artist.name}
								</Heading>
							</VStack>
						))}
						{loading && (
							<Center h="100%">
								<LoadingSpinner />
							</Center>
						)}
					</Grid>

					<Flex
						w="100%"
						alignItems="center"
						justifyContent="center"
						mt="spacing.l"
					>
						<Button
							isDisabled={isDisabled}
							onClick={() => validateOnboardingArtistsPickerStep()}
						>
							{copy}
						</Button>
					</Flex>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};

export default ArtistPickerDrawer;
